import React, { useState, useEffect } from 'react';
import { X, RotateCcw, AlertTriangle, ChevronDown } from 'lucide-react';
import { Transaction } from '../../types';

const REASON_OPTIONS = [
  { value: 'Defective Item',         label: 'Defective Item' },
  { value: 'Wrong Size',             label: 'Wrong Size' },
  { value: 'Customer Changed Mind',  label: 'Customer Changed Mind' },
  { value: 'Other',                  label: 'Other…' },
];

interface ReturnReasonModalProps {
  isOpen: boolean;
  /**
   * Pass a Transaction to pre-fill (from Transactions tab).
   * Pass null to show a manual transaction-ID input (from Returns ledger).
   */
  transaction: Transaction | null | undefined;
  onClose: () => void;
  /**
   * reason  — selected dropdown value (or the custom text when "Other" is chosen)
   * notes   — free-form additional detail (required when reason = "Other")
   * manualTransactionId — only present when transaction prop is null
   */
  onConfirm: (reason: string, notes: string | null, manualTransactionId?: string) => void;
}

const ReturnReasonModal: React.FC<ReturnReasonModalProps> = ({
  isOpen,
  transaction,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [manualTxnId, setManualTxnId] = useState('');

  // Reset form every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setOtherText('');
      setManualTxnId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // null = manual entry mode; Transaction = pre-filled mode
  const isManualEntry = transaction == null;
  const isOther = reason === 'Other';

  const canConfirm =
    reason !== '' &&
    (!isManualEntry || manualTxnId.trim() !== '') &&
    (!isOther || otherText.trim() !== '');

  const handleConfirm = () => {
    if (!canConfirm) return;
    const finalReason = isOther ? otherText.trim() : reason;
    const finalNotes = isOther ? null : null; // notes field reserved for future use
    const txnId = isManualEntry ? manualTxnId.trim() : undefined;
    onConfirm(finalReason, finalNotes, txnId);
  };

  const txnShort = transaction
    ? (transaction.transactionId ?? transaction.id ?? '').slice(0, 8) + '…'
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-rose-400 to-rose-600" />

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center">
                <RotateCcw size={18} className="text-rose-500" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">Process Replacement</h2>
                {txnShort && (
                  <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                    TXN {txnShort}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>

          {/* Manual transaction ID input */}
          {isManualEntry && (
            <div className="mb-4">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                Transaction ID <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Paste transaction UUID…"
                value={manualTxnId}
                onChange={e => setManualTxnId(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500 dark:text-white font-mono transition-all"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Copy the transaction ID from the Store Sales tab.
              </p>
            </div>
          )}

          {/* Reason dropdown */}
          <div className="mb-4">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
              Return Reason <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <select
                value={reason}
                onChange={e => { setReason(e.target.value); setOtherText(''); }}
                className="w-full px-3 py-2.5 pr-9 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500 dark:text-white appearance-none transition-all"
              >
                <option value="">Select a reason…</option>
                {REASON_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* "Other" text area — slides in */}
          {isOther && (
            <div className="mb-4">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                Please Describe <span className="text-rose-500">*</span>
              </label>
              <textarea
                placeholder="Describe the reason for this return…"
                value={otherText}
                onChange={e => setOtherText(e.target.value)}
                rows={3}
                maxLength={95}
                autoFocus
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500 dark:text-white resize-none transition-all"
              />
              <p className="text-[10px] text-gray-400 text-right mt-0.5">
                {otherText.length}/95
              </p>
            </div>
          )}

          {/* Warning banner */}
          <div className="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl mb-6">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              This will reverse the original sale and restock items. This action <strong>cannot be undone</strong>.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-bold hover:bg-rose-600 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              Confirm Replacement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnReasonModal;
